/**
 * @file form-helpers.ts
 * @description Shared form-validation and API-error-mapping helpers used by
 *   the auth pages. Centralizes Zod error → field-error translation so each
 *   page component stays small.
 * @module Lib
 * @security Validation here is UX-only; the server is the source of truth.
 *   Errors returned to the UI never include raw API detail strings (they
 *   pass through i18n keys instead).
 */

import type { TFunction } from 'i18next';
import { z } from 'zod';
import { ApiError } from './api';

export const CredentialsSchema = z.object({
  email: z.string().email(),
  password: z.string().min(12),
});

export interface FieldErrors {
  email?: string;
  password?: string;
  form?: string;
}

export function validateCredentials(email: string, password: string, t: TFunction): FieldErrors {
  const parsed = CredentialsSchema.safeParse({ email, password });
  if (parsed.success) return {};
  const out: FieldErrors = {};
  for (const issue of parsed.error.issues) {
    const f = issue.path[0];
    if (f === 'email') out.email = t('login.email_invalid');
    if (f === 'password') out.password = t('login.password_too_short');
  }
  return out;
}

export function mapLoginError(err: unknown, t: TFunction): string {
  if (err instanceof ApiError && err.status === 423) return t('login.locked');
  if (err instanceof ApiError && err.status === 429) return t('login.rate_limited');
  return t('login.invalid_credentials');
}

/**
 * The server returns a 400 with details=[{code: 'PASSWORD_BREACHED'}] when
 * HaveIBeenPwned matches the candidate password. Detect that specific
 * case so the UI can show a targeted message ("this password is in known
 * breaches; pick another") instead of the generic invitation/conflict
 * fallback.
 */
export function isPasswordBreachedError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 400) return false;
  return (err.problem.errors ?? []).some((d) => d.code === 'PASSWORD_BREACHED');
}

export function mapRegisterError(err: unknown, t: TFunction): string {
  if (isPasswordBreachedError(err)) return t('register.password_breached');
  if (err instanceof ApiError && err.status === 400) return t('register.invalid_invitation');
  if (err instanceof ApiError && err.status === 409) return t('register.conflict');
  return t('register.generic_error');
}
